import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Award, Users, TrendingUp } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Reliability',
      description: 'Consistent quality and on-time delivery you can count on'
    },
    {
      icon: Award,
      title: 'Quality',
      description: 'OEM-grade parts meeting the highest industry standards'
    },
    {
      icon: Users,
      title: 'Expertise',
      description: 'Decades of experience in heavy machinery parts supply'
    }
  ];

  const statistics = [
    { number: '15+', label: 'Years in Business' },
    { number: '50K+', label: 'Parts Supplied' },
    { number: '2000+', label: 'Satisfied Customers' },
    { number: '99%', label: 'Quality Rating' }
  ];

  return (
    <section id="about" className="py-16 sm:py-20 lg:py-24 bg-[#2d2d2d]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1583737177718-b95af4095782"
                alt="Industrial machinery parts warehouse"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/60 to-transparent" />
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="absolute -bottom-6 -right-6 bg-[#ff6b35] text-white p-6 rounded-xl shadow-2xl"
            >
              <TrendingUp size={32} className="mb-2" />
              <p className="text-3xl font-bold">15+</p>
              <p className="text-sm">Years Excellence</p>
            </motion.div>
          </motion.div>

          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                About Our Company
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed mb-6">
                We are a leading supplier of quality <span className="text-[#ff6b35]">Trade Zone Equipments</span> parts, dedicated to providing our clients with reliable components that ensure optimal machinery performance and longevity.
              </p>
              <p className="text-lg text-gray-400 leading-relaxed">
                Our mission is to deliver excellence through premium parts, expert knowledge, and exceptional customer service. We understand the critical role that quality parts play in your operations, and we're committed to being your trusted partner.
              </p>
            </div>

            {/* Values */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {values.map((value, index) => {
                const IconComponent = value.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
                    className="text-center"
                  >
                    <div className="w-16 h-16 bg-[#ff6b35]/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <IconComponent size={28} className="text-[#ff6b35]" />
                    </div>
                    <h3 className="text-white font-bold mb-2">{value.title}</h3>
                    <p className="text-sm text-gray-400">{value.description}</p>
                  </motion.div>
                );
              })}
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-[#4a4a4a]">
              {statistics.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                  className="text-center"
                >
                  <p className="text-3xl sm:text-4xl font-bold text-[#ff6b35] mb-2">
                    {stat.number}
                  </p>
                  <p className="text-sm text-gray-400">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;